/*
 * Copyright (c) 2017 Honeywell International, Inc. All rights reserved.
 *
 * This file contains trade secrets of Honeywell International, Inc.  No part
 *  may be reproduced or transmitted in any form by any means or for any
 *  purpose without the express written permission of Honeywell.
 *
 */

import { Injectable } from '@angular/core';
import { Subject } from 'rxjs/Subject';
import { initialize, LDClient, LDFlagSet, LDUser, LDFlagValue } from 'launchdarkly-js-client-sdk';
import { environment } from '../../environments/environment';
import { FeatureFlags } from '../constants/feature-flags.const';

@Injectable()
export class LaunchDarklyService {
    ldClient: LDClient;
    flags: LDFlagSet;
    user: LDUser;
    flagNames: Array<string>;
    flagChange: Subject<Object> = new Subject<Object>();

    constructor() {
        this.initFeatureFlags();
        const clientId = environment.launchDarklyClientId;
        const userKey = environment.launchDarklyUserKey;
        const organizationId = sessionStorage.getItem('organizationid');
        this.user = {
            key: userKey,
            anonymous: true
        };
        this.user['custom'] = { organization: organizationId };
        this.ldClient = this.initDarklyClient(clientId, this.user);
        this.ldClient.on('change', (flags) => {
            this.updateFlags(flags);
        });

        this.ldClient.on('ready', () => {
            this.setFlags();
        });
    }

    initFeatureFlags() {
        this.flagNames = [
            FeatureFlags.ASSIGN_PROPERTY_ROLE_FROM_IDENTIFY_ASSETS,
            FeatureFlags.ASSIGN_LOCATION_FROM_IDENTIFY_ASSETS,
            FeatureFlags.HISTORY_VALIDATION,
            FeatureFlags.PUBLISH_HISTORY_DATA,
            FeatureFlags.AUTOMATIC_ANALYTICS_SETUP,
            FeatureFlags.PUBLISH_ASSET_MODEL,
            FeatureFlags.IMPROVED_CONTEXT_DISCOVERY_ACCURACY,
            FeatureFlags.UNIFIED_WORKFLOW,
            FeatureFlags.COR_TOOL_ACCESS,
            FeatureFlags.GATEWAY_CONFIG,
            FeatureFlags.EVENT_LOGGING,
            FeatureFlags.CDT_LANGUAGE_CONFIGURATION,
            FeatureFlags.GATEWAY_TENANT
        ];
        this.flags = {};
    }

    initDarklyClient(clientId: string, user: LDUser) {
        const ldClient = initialize(clientId, user, { evaluationReasons: true });
        return ldClient;
    }

    setFlags() {
        this.updateFlags(this.ldClient.allFlags());
    }

    updateFlags(flags) {
        this.flagNames.forEach(flagName => {
            if (flags[flagName] !== undefined) {
                this.flags[flagName] = flags[flagName];
            }
        });

        this.flagChange.next(this.flags);
    }

    changeUser(user) {
        if (user !== 'Anonymous') {
            this.ldClient.identify({ key: user, name: user, anonymous: false });
        } else {
            this.ldClient.identify({ key: 'anon', anonymous: true });
        }
    }

    private getFeatureFlagValue(featureFlagName: string, defaultValue: LDFlagValue, fetchCurrent?: boolean) {
        if (!this.flags[featureFlagName]) {
            return defaultValue;
        }
        if (fetchCurrent) {
            if (this.flags[featureFlagName].hasOwnProperty('current')) {
                return this.flags[featureFlagName].current;
            } else {
                return this.flags[featureFlagName];
            }
        } else {
            if (this.flags[featureFlagName]) {
                return this.flags[featureFlagName];
            } else {
                return defaultValue;
            }
        }
    }

    getBooleanFeatureFlagValue(featureFlagName: string, fetchCurrent?: boolean) {
        return this.getFeatureFlagValue(featureFlagName, false, fetchCurrent);
    }

    getJsonFeatureFlagValue(featureFlagName: string, fetchCurrent?: boolean) {
        const receivedJsonValue = this.getFeatureFlagValue(featureFlagName, [], fetchCurrent);
        return { enabled: receivedJsonValue.length > 0, value: receivedJsonValue };
    }
}
